"use client";

import { Check, LinkIcon } from "lucide-react";
import { useState, useSyncExternalStore } from "react";

const EMPTY_SUBSCRIBE = () => () => {};

function FacebookIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <path d="M8.5 14V7.5h2.2l.3-2.6H8.5V3.3c0-.7.2-1.2 1.2-1.2h1.3V0H9.2C7.2 0 6 1.2 6 3.1v1.8H4V7.5h2V14h2.5z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
      <path d="M12.96 0H1.04C.47 0 0 .46 0 1.03v11.93C0 13.53.47 14 1.04 14h11.92c.58 0 1.04-.47 1.04-1.04V1.03C14 .46 13.54 0 12.96 0zM4.15 11.93H2.08V5.27h2.07v6.66zM3.11 4.36c-.66 0-1.2-.54-1.2-1.2 0-.67.54-1.2 1.2-1.2.66 0 1.2.53 1.2 1.2 0 .66-.54 1.2-1.2 1.2zM11.93 11.93h-2.07V8.7c0-.77-.02-1.76-1.07-1.76-1.07 0-1.24.84-1.24 1.7v3.29H5.48V5.27h1.99v.91h.03c.28-.52.95-1.07 1.96-1.07 2.1 0 2.49 1.38 2.49 3.17v3.65z" />
    </svg>
  );
}

type ShareTarget = {
  label: string;
  Icon: () => React.ReactElement;
  build: (url: string, title: string) => string;
};

const TARGETS: ShareTarget[] = [
  {
    label: "Facebook",
    Icon: FacebookIcon,
    build: (url) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    label: "LinkedIn",
    Icon: LinkedInIcon,
    build: (url) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
];

export function BlogShareRail({ title }: { title: string }) {
  // Empty during SSR/first hydration, then the live URL after mount —
  // keeps share targets in sync with client-side navigation.
  const url = useSyncExternalStore(
    EMPTY_SUBSCRIBE,
    () => window.location.href,
    () => "",
  );
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
    }
  };

  const button =
    "flex size-10 items-center justify-center rounded-full bg-dusk-02 text-purple-04 transition-colors hover:bg-purple-03 hover:text-white";

  return (
    <>
      <div
        role="group"
        aria-label="Share this post"
        className="pointer-events-none absolute top-0 left-[-88px] hidden h-full lg:block"
      >
        <div className="pointer-events-auto sticky top-[120px] flex flex-col items-center gap-2">
          <span className="mb-1 text-caption font-bold tracking-wider text-black/55 uppercase">
            Share
          </span>
          <button
            type="button"
            onClick={copy}
            aria-label={copied ? "Link copied" : "Copy link"}
            className={button}
          >
            {copied ? (
              <Check className="size-[18px]" strokeWidth={2} aria-hidden />
            ) : (
              <LinkIcon className="size-[18px]" strokeWidth={1.75} aria-hidden />
            )}
          </button>
          {TARGETS.map(({ label, Icon, build }) => (
            <a
              key={label}
              href={url ? build(url, title) : "#"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${label}`}
              className={button}
            >
              <Icon />
            </a>
          ))}
        </div>
      </div>

      <div className="mb-8 flex items-center gap-2 lg:hidden">
        <span className="text-caption font-bold tracking-wider text-black/55 uppercase">
          Share
        </span>
        <button type="button" onClick={copy} aria-label={copied ? "Link copied" : "Copy link"} className={button}>
          {copied ? (
            <Check className="size-[18px]" strokeWidth={2} aria-hidden />
          ) : (
            <LinkIcon className="size-[18px]" strokeWidth={1.75} aria-hidden />
          )}
        </button>
        {TARGETS.map(({ label, Icon, build }) => (
          <a
            key={label}
            href={url ? build(url, title) : "#"}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on ${label}`}
            className={button}
          >
            <Icon />
          </a>
        ))}
      </div>
    </>
  );
}
